import type { z } from 'zod';

// Discord capabilities (design §6.6). A capability is something a worker can do in
// Discord, such as `voice.move`. It is defined once, in shared code, with schemas
// for its payload and its result: the server validates the payload when a command
// is staged and the result when a worker reports it, and the worker parses the
// payload before running its handler. Base capabilities are core's (./base.ts); a
// module's composite capabilities are named `<module>.<name>` and live in its
// `shared/` code.

const CAPABILITY: unique symbol = Symbol.for('itc.capability');

export interface CapabilityDefinition<P extends z.ZodType, R extends z.ZodType> {
  /** Lower-case and dotted, at least two segments: `voice.move`, `fleet.launch_voice`. */
  name: string;
  payload: P;
  result: R;
  /**
   * Running it twice has the same effect as once. Only idempotent commands are retried
   * automatically after a worker disconnects mid-command (design §6.6).
   */
  idempotent: boolean;
  /** Store the result on the command record (default true). Off for large results needed only once. */
  storeResult?: boolean;
}

export interface CapabilityDef<P extends z.ZodType = z.ZodType, R extends z.ZodType = z.ZodType> {
  readonly [CAPABILITY]: true;
  readonly name: string;
  readonly payload: P;
  readonly result: R;
  readonly idempotent: boolean;
  readonly storeResult: boolean;
}

export const CAPABILITY_NAME = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;

export function defineCapability<P extends z.ZodType, R extends z.ZodType>(definition: CapabilityDefinition<P, R>): CapabilityDef<P, R> {
  if (!CAPABILITY_NAME.test(definition.name) || definition.name.length > 64) {
    throw new Error(`Capability "${definition.name}" must be lower-case and dotted, with at least two segments (at most 64 characters)`);
  }
  return Object.freeze({
    [CAPABILITY]: true as const,
    name: definition.name,
    payload: definition.payload,
    result: definition.result,
    idempotent: definition.idempotent,
    storeResult: definition.storeResult ?? true,
  });
}

export function isCapabilityDef(value: unknown): value is CapabilityDef {
  return typeof value === 'object' && value !== null && CAPABILITY in value;
}

/** What a caller passes. */
export type CapabilityPayload<C extends CapabilityDef> = z.input<C['payload']>;

/** What a caller receives once a worker has done it. */
export type CapabilityResult<C extends CapabilityDef> = z.output<C['result']>;
